import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';
import { emulatorEndpoints } from '@etiquetachef/ui-shared/emulator';

// Config Firebase Web — valores são públicos por design (não são segredo),
// mas mantidos em GitHub Secrets para evitar pin no repo. Vite injeta via
// import.meta.env no build; em CI esses VITE_FIREBASE_* vêm dos secrets do
// workflow (release.yml, step "Build hosting apps"). Sem isso o bundle
// saía com apiKey:"" e quebrava em main.tsx (auth/invalid-api-key → tela
// branca). Em dev local, copiar `.env.example` para `.env`.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
};

const FUNCTIONS_REGION = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION ?? '';

/**
 * App Firebase do admin (Auth, Firestore, Functions, Storage saem dele).
 *
 * Origem: sem issue — commit 4dbd8df25, 2026-04-26 (scaffolding do admin);
 * modo Functions locais em 7457c3063 (#785).
 *
 * Usado por: todo `lib/firestore*.ts`, `lib/callables.ts`, `auth/*`,
 * `lib/webPushController.ts` e o import por OCR do financeiro (`storage`).
 *
 * Arquitetura: Firestore e callables do admin dependem do claim `superAdmin`
 * (rules e `ADMIN_CALLABLE_OPTIONS`); este módulo só conecta, não autoriza.
 * Região das callables vem de `VITE_FIREBASE_FUNCTIONS_REGION`
 * (`southamerica-east1`).
 */
export const firebaseApp: FirebaseApp = initializeApp(firebaseConfig);

// Firebase App Check (reCAPTCHA Enterprise). Mesmo Web App "shared" do
// apps/app — um único registro de App Check cobre os dois (mesmo appId) e a
// mesma site key vale aqui (admin.etiquetachef.com está nos domínios da key).
// Guard pela env: sem ela App Check não inicializa. Em DEV, debug token (ver
// Firebase Console → App Check → Apps → Manage debug tokens).
const RECAPTCHA_ENTERPRISE_SITE_KEY =
  import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY ?? '';
if (import.meta.env.DEV) {
  (
    globalThis as typeof globalThis & { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }
  ).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}
if (RECAPTCHA_ENTERPRISE_SITE_KEY) {
  initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_ENTERPRISE_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth: Auth = getAuth(firebaseApp);
export const firestore: Firestore = getFirestore(firebaseApp);
export const functions: Functions = getFunctions(firebaseApp, FUNCTIONS_REGION);

// Functions LOCAIS contra a PRODUÇÃO (#785, 2026-09-30 15:51 +0200 — mesmo
// modo que o CRM ganhou na #776). Só as callables vão para o emulador
// (`firebase emulators:start --only functions`); Auth, Firestore e Storage
// continuam os de produção, então o login é o do super-admin real e as
// Functions leem o banco real. É como se testa Function antes do deploy
// (CLAUDE.md, "Functions: testar LOCAL, contra os recursos de PRODUÇÃO").
// Cuidado: ação que grava, grava DE VERDADE.
// O `import.meta.env.DEV &&` vem primeiro: no `vite build` ele é a constante
// `false` e o bloco sai do bundle — nenhum build de produção aponta para
// localhost por uma env que vazou. O Vite roda na porta padrão 5173, que
// está no CORS de `ADMIN_CALLABLE_OPTIONS`.
//
// #1011 (2026-10-20 15:58 +0200): o ramo `VITE_USE_EMULATORS` (tudo emulado) fica
// DEPOIS do `export const storage`, abaixo. Porta do emulador de Functions vem
// de `VITE_EMULATOR_FUNCTIONS_PORT` (padrão 5001).
if (
  import.meta.env.DEV &&
  import.meta.env.VITE_USE_EMULATORS !== 'true' &&
  import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true'
) {
  const emu = emulatorEndpoints(import.meta.env);
  console.warn(
    `[firebase] Functions LOCAIS (${emu.host}:${String(emu.functionsPort)}) contra Auth/Firestore/Storage de PRODUÇÃO`,
  );
  connectFunctionsEmulator(functions, emu.host, emu.functionsPort);
}
// Storage — usado pelo import por OCR do financeiro (#75): o super-admin sobe
// a imagem do extrato em `finance_imports/{importId}/...` (rules gateiam por
// custom claim). Único uso de Storage no admin web hoje.
export const storage: FirebaseStorage = getStorage(firebaseApp);

// Emulators Suite completa (Auth, Firestore, Functions, Storage) — modo dos
// slots `full` do pool Docker (#1011, 2026-10-20 15:58 +0200), o mesmo toggle
// que `apps/app` e `apps/crm` já tinham. Nada aqui fala com produção. O
// `import.meta.env.DEV &&` vem PRIMEIRO pelo motivo de `apps/app/src/lib/
// firebase.ts`: no `vite build` ele é `false`, o bloco sai do bundle e nenhum
// build de produção liga emulador por uma env que vazou. Deve ficar depois de
// `storage`, que é exportado acima.
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  const emu = emulatorEndpoints(import.meta.env);
  console.info('[firebase] conectando aos emuladores locais (VITE_USE_EMULATORS=true)');
  connectAuthEmulator(auth, emu.authUrl, { disableWarnings: true });
  connectFirestoreEmulator(firestore, emu.host, emu.firestorePort);
  connectFunctionsEmulator(functions, emu.host, emu.functionsPort);
  connectStorageEmulator(storage, emu.host, emu.storagePort);
}
